import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type PointerEvent as ReactPointerEvent } from 'react';
import {
  copyHitsRequired,
  effectDensity,
  enemyDamage,
  enemyFireDelay,
  finaleBossHealth,
  finaleLevelCount,
  finalePlayerKey,
  finalePowerMax,
  finaleReloadEvery,
  finaleScoreKey,
  finaleSeedScores,
  finaleWeapons,
  levelSpeed,
  levelTargetCount,
  poweredCooldown,
  poweredDamage,
  type FinaleScoreEntry,
  type FinaleWeaponId,
} from '../data/finaleGame';
import { attractAudio, audioAsset } from '../lib/attractAudio';
import { useAudioPreferences } from '../lib/audioPreferences';
import { getCabinetId } from '../lib/easterEggProgress';
import type { CharacterProfile } from '../types';
import { FightImpactOverlay } from './FightImpactOverlay';
import { FinaleGameHud, type FinalePhase } from './FinaleGameHud';
import { FinaleLeaderboard } from './FinaleLeaderboard';
import { IdleFighterSprite } from './IdleFighterSprite';

interface FinaleShot { id: number; originX: number; originY: number; angle: number; length: number; hit: boolean; power: number; weaponId: FinaleWeaponId; }
interface FinaleImpact { id: number; x: number; y: number; power: number; boss: boolean; density: number; }
interface FinalePowerUp { id: number; x: number; y: number; type: 'power' | 'rapid' | 'shield' | 'medkit' | 'bomb'; }
interface EnemyBolt { id: number; x: number; y: number; damage: number; }
interface ArenaTarget { key: string; character: CharacterProfile; isCopy: boolean; x: number; bottom: number; scale: number; row: number; }

const sfx = (name: string) => audioAsset(`/assets/audio/finale-game/${name}.wav`);
const voice = (speaker: string, name: string) => audioAsset(`/assets/audio/finale-voices/${speaker}/${name}.wav`);
const rankScores = (scores: FinaleScoreEntry[]) => [...new Map(scores.map((entry) => [`${entry.name.toUpperCase()}|${entry.score}|${entry.recordedAt.replace('.000Z', 'Z')}`, entry])).values()].sort((left, right) => right.score - left.score || left.elapsedMs - right.elapsedMs).slice(0, 5);
const readScores = (): FinaleScoreEntry[] => {
  try { return rankScores([...finaleSeedScores, ...JSON.parse(localStorage.getItem(finaleScoreKey) ?? '[]') as FinaleScoreEntry[]]); } catch { return finaleSeedScores; }
};
const seeded = (value: number) => {
  const raw = Math.sin(value * 12.9898 + 78.233) * 43758.5453;
  return raw - Math.floor(raw);
};
const powerUpNames: Record<FinalePowerUp['type'], string> = { power: 'PLASMA CORE', rapid: 'RAPID COIL', shield: 'SHIELD PATCH', medkit: 'OUCH KIT', bomb: 'GIB BOMB' };

export function FinaleAttractScene({ onRestart, onRetryShooter, onPlayFightingGame, characters }: { onRestart: () => void; onRetryShooter: () => void; onPlayFightingGame: () => void; characters: CharacterProfile[] }) {
  const roster = useMemo(() => characters.filter((character) => character.id !== 'the-convergence'), [characters]);
  const reducedMotion = useMemo(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);
  const debugResult = useMemo(() => import.meta.env.DEV ? new URLSearchParams(window.location.search).get('finaleResult') : null, []);
  const debugLevel = useMemo(() => import.meta.env.DEV ? Number(new URLSearchParams(window.location.search).get('finaleLevel') ?? 0) : 0, []);
  const arenaRef = useRef<HTMLElement>(null);
  const timersRef = useRef<Set<number>>(new Set());
  const startTimeRef = useRef(Date.now());
  const effectIdRef = useRef(0);
  const bulletRef = useRef(0);
  const runSeedRef = useRef(Math.floor(Math.random() * 1_000_000_000));
  const reportedPhaseRef = useRef<FinalePhase | null>(null);
  const [phase, setPhase] = useState<FinalePhase>(debugResult === 'loss' ? 'game-over' : debugResult === 'victory' ? 'victory' : debugLevel ? 'levels' : 'select');
  const [selected, setSelected] = useState<CharacterProfile>(roster[0]);
  const [fightVisible, setFightVisible] = useState(false);
  const [fightExplosion, setFightExplosion] = useState(false);
  const [level, setLevel] = useState(debugResult === 'loss' ? 7 : debugResult === 'victory' ? finaleLevelCount : debugLevel || 1);
  const [levelCleared, setLevelCleared] = useState(false);
  const [shuffleNonce, setShuffleNonce] = useState(0);
  const [copyHits, setCopyHits] = useState(0);
  const [exploding, setExploding] = useState<Set<string>>(() => new Set());
  const [revealedCopies, setRevealedCopies] = useState<Set<string>>(() => new Set());
  const [weaponId, setWeaponId] = useState<FinaleWeaponId>('plasma-rifle');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [score, setScore] = useState(debugResult ? 17500 : 0);
  const [bossHealth, setBossHealth] = useState(finaleBossHealth);
  const [health, setHealth] = useState(100);
  const [shield, setShield] = useState(0);
  const [powerLevel, setPowerLevel] = useState(1);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [clock, setClock] = useState(Date.now());
  const [finishedElapsedMs, setFinishedElapsedMs] = useState(debugResult ? 118700 : 0);
  const [shots, setShots] = useState<FinaleShot[]>([]);
  const [impacts, setImpacts] = useState<FinaleImpact[]>([]);
  const [powerUps, setPowerUps] = useState<FinalePowerUp[]>([]);
  const [enemyBolts, setEnemyBolts] = useState<EnemyBolt[]>([]);
  const [firingSequence, setFiringSequence] = useState(0);
  const [reloading, setReloading] = useState(false);
  const [hitSequence, setHitSequence] = useState(0);
  const [godMode, setGodMode] = useState(false);
  const [playerName, setPlayerName] = useState(() => localStorage.getItem(finalePlayerKey) ?? '');
  const [bestScores, setBestScores] = useState<FinaleScoreEntry[]>(readScores);
  const [recordSaved, setRecordSaved] = useState(false);
  const [runReportId, setRunReportId] = useState<number | null>(null);
  const [scoreStatus, setScoreStatus] = useState('CONNECTING TO CABINET BOARD');
  const [audioPreferences, updateAudioPreferences] = useAudioPreferences();
  const weapon = finaleWeapons.find((candidate) => candidate.id === weaponId) ?? finaleWeapons[0];
  const copyGoal = copyHitsRequired(level);
  const cooldownDurationMs = godMode ? 0 : poweredCooldown(weapon.cooldownMs, powerLevel);
  const cooldownMs = godMode ? 0 : Math.max(0, cooldownUntil - clock);
  const damage = poweredDamage(weapon.damage, powerLevel);
  const combatActive = phase === 'levels' || phase === 'boss';
  const elapsedMs = phase === 'boss-defeat' || phase === 'victory' || phase === 'game-over' ? finishedElapsedMs : combatActive ? Math.max(0, clock - startTimeRef.current) : 0;

  const schedule = (callback: () => void, delayMs: number) => {
    const timer = window.setTimeout(() => { timersRef.current.delete(timer); callback(); }, delayMs);
    timersRef.current.add(timer);
  };
  useEffect(() => () => { timersRef.current.forEach((timer) => window.clearTimeout(timer)); timersRef.current.clear(); }, []);
  useEffect(() => {
    if (!combatActive) return;
    const interval = window.setInterval(() => setClock(Date.now()), cooldownMs > 0 ? 40 : 180);
    return () => window.clearInterval(interval);
  }, [combatActive, cooldownMs]);

  useEffect(() => {
    let live = true;
    void fetch('/api/finale-scores', { headers: { Accept: 'application/json' } }).then(async (response) => {
      if (!response.ok) throw new Error('scoreboard-unavailable');
      const payload = await response.json() as { scores?: FinaleScoreEntry[] };
      if (live && payload.scores?.length) { setBestScores(rankScores(payload.scores)); setScoreStatus('GLOBAL CABINET BOARD'); }
    }).catch(() => { if (live) setScoreStatus('CABINET BOARD OFFLINE // LOCAL SCORES'); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if ((phase !== 'victory' && phase !== 'game-over') || reportedPhaseRef.current === phase) return;
    reportedPhaseRef.current = phase;
    const fallbackName = (playerName.trim() || selected.name).slice(0, 15).toUpperCase();
    const entry: FinaleScoreEntry = { name: fallbackName, score, elapsedMs, cleared: phase === 'victory' ? finaleLevelCount : level, bossDefeated: phase === 'victory', powerLevel, godMode, recordedAt: new Date().toISOString() };
    const localNext = rankScores([...bestScores, entry]);
    setBestScores(localNext);
    try { localStorage.setItem(finaleScoreKey, JSON.stringify(localNext)); } catch { /* D1 remains the primary board. */ }
    void fetch('/api/finale-scores', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cabinetId: getCabinetId(), playerName: fallbackName, score, elapsedMs: Math.round(elapsedMs), cleared: entry.cleared, outcome: phase, fighterId: selected.id, powerLevel, godMode }),
      keepalive: true,
    }).then(async (response) => {
      if (!response.ok) throw new Error('run-report-failed');
      const payload = await response.json() as { runId?: number; scores?: FinaleScoreEntry[]; discordDelivered?: boolean };
      if (payload.runId) setRunReportId(payload.runId);
      if (payload.scores?.length) setBestScores(rankScores(payload.scores));
      setScoreStatus(payload.discordDelivered ? 'RUN SAVED // DISCORD NOTIFIED' : 'RUN SAVED // DISCORD DELIVERY PENDING');
    }).catch(() => setScoreStatus('RUN SAVED LOCALLY // NETWORK RETRY NEEDED'));
  }, [bestScores, elapsedMs, godMode, level, phase, playerName, powerLevel, score, selected.id, selected.name]);

  const play = (name: string, playbackRate = 1, gain = 0.9) => { void attractAudio.play(sfx(name), { kind: 'effect', gain, playbackRate }); };
  const speak = (name: string, gain = 1) => { void attractAudio.play(voice(selected.id, name), { kind: 'shooter-voice', gain }); };

  const arenaTargets = useMemo<ArenaTarget[]>(() => {
    if (phase !== 'levels') return [];
    const targetCount = levelTargetCount(level);
    const ordered = [...roster].sort((left, right) => seeded(level * 101 + roster.indexOf(left) * 19) - seeded(level * 101 + roster.indexOf(right) * 19));
    const cloneCharacter = ordered[(level * 5) % ordered.length];
    const active = ordered.slice(0, targetCount);
    if (!active.some((candidate) => candidate.id === cloneCharacter.id)) active[0] = cloneCharacter;
    const entries = [...active.map((character) => ({ character, isCopy: false })), { character: cloneCharacter, isCopy: true }];
    const copySlot = (Math.floor(seeded(runSeedRef.current + level * 701) * entries.length) + shuffleNonce * 3) % entries.length;
    const originalSlots = entries.map((_, index) => index).filter((slot) => slot !== copySlot).sort((left, right) => seeded(runSeedRef.current + level * 701 + shuffleNonce * 97 + left * 37) - seeded(runSeedRef.current + level * 701 + shuffleNonce * 97 + right * 37));
    const slots = [...originalSlots, copySlot];
    return entries.map((entry, index) => {
      const columns = Math.min(10, Math.ceil(entries.length / 2));
      const slot = slots[index];
      const row = Math.floor(slot / columns);
      const column = slot % columns;
      const jitter = (seeded(runSeedRef.current + level * 307 + shuffleNonce * 41 + slot * 13) - 0.5) * 4;
      return {
        key: `${entry.isCopy ? 'copy' : 'original'}-${entry.character.id}`,
        ...entry,
        x: 5.5 + column * (89 / Math.max(1, columns - 1)) + jitter,
        bottom: 2 + row * 22 + seeded(index + level * 73) * 5,
        scale: Math.max(0.68, 1 - row * 0.16),
        row,
      };
    });
  }, [level, phase, roster, shuffleNonce]);

  const updateAim = (event: ReactPointerEvent<HTMLElement>) => {
    const horizontal = event.clientX / Math.max(1, window.innerWidth) - 0.5;
    const vertical = event.clientY / Math.max(1, window.innerHeight) - 0.5;
    const style = arenaRef.current?.style;
    style?.setProperty('--weapon-shift-x', `${horizontal * 62}px`);
    style?.setProperty('--weapon-shift-y', `${vertical * 38}px`);
    style?.setProperty('--weapon-rotation', `${horizontal * 8 - vertical * 4}deg`);
    style?.setProperty('--aim-x', `${event.clientX}px`);
    style?.setProperty('--aim-y', `${event.clientY}px`);
  };

  const addImpact = (x: number, y: number, power: number, boss = false, density = effectDensity(level)) => {
    const id = ++effectIdRef.current;
    const preferredDensity = Math.min(108, Math.round(density * audioPreferences.shooterGore));
    setImpacts((current) => [...current.slice(-11), { id, x, y, power: Math.min(12, power), boss, density: reducedMotion ? Math.min(18, preferredDensity) : preferredDensity }]);
    schedule(() => setImpacts((current) => current.filter((impact) => impact.id !== id)), reducedMotion ? 380 : 1450);
  };

  const startReload = () => {
    setReloading(true);
    setCooldownUntil(Date.now() + 1050);
    speak(`reload-${1 + Math.floor(bulletRef.current / finaleReloadEvery) % 3}`);
    play('weapon-equip', 0.68, 0.7);
    schedule(() => { setReloading(false); setCooldownUntil(0); }, 1050);
  };

  const fire = (clientX: number, clientY: number, hit: boolean) => {
    const horizontal = clientX / Math.max(1, window.innerWidth) - 0.5;
    const vertical = clientY / Math.max(1, window.innerHeight) - 0.5;
    const originX = window.innerWidth * 0.87 + horizontal * 24;
    const originY = window.innerHeight * 0.86 + vertical * 18;
    const id = ++effectIdRef.current;
    setShots((current) => [...current.slice(-11), { id, originX, originY, angle: Math.atan2(clientY - originY, clientX - originX) * 180 / Math.PI, length: Math.hypot(clientX - originX, clientY - originY), hit, power: Math.min(20, powerLevel + Math.floor(level / 2)), weaponId }]);
    setFiringSequence((current) => current + 1);
    bulletRef.current += 1;
    if (!godMode) setCooldownUntil(Date.now() + cooldownDurationMs);
    play('plasma-shot', 0.82 + level * 0.018 + powerLevel * 0.016, 0.78);
    schedule(() => setShots((current) => current.filter((candidate) => candidate.id !== id)), 850);
    if (bulletRef.current % finaleReloadEvery === 0) schedule(startReload, cooldownDurationMs + 30);
  };

  const fireMiss = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0 || cooldownMs > 0 || reloading || !combatActive) return;
    fire(event.clientX, event.clientY, false);
  };

  const spawnPowerUp = (x: number, y: number, nextLevel: number) => {
    const types: FinalePowerUp['type'][] = ['power', 'rapid', 'shield', 'medkit', 'bomb'];
    const id = ++effectIdRef.current;
    setPowerUps((current) => [...current.slice(-3), { id, x, y, type: types[(nextLevel - 1) % types.length] }]);
    schedule(() => setPowerUps((current) => current.filter((candidate) => candidate.id !== id)), 9000);
  };

  const finishCopyLevel = (characterId: string, x: number, y: number, targetKey: string) => {
    setExploding((current) => new Set(current).add(targetKey));
    setLevelCleared(true);
    setScore((current) => current + weapon.score * level * powerLevel + 400 * level);
    addImpact(x, y, 2 + level * 0.5, false, effectDensity(level));
    play('target-hit', 0.9 + level * 0.025);
    schedule(() => play('vaporize', 0.84 + level * 0.018), 75);
    if (level % 2 === 0) schedule(() => speak(`quip-${characterId}`), 220);
    if (level < finaleLevelCount - 1) {
      spawnPowerUp(x, y, level + 1);
      schedule(() => {
        setLevel((current) => current + 1);
        setCopyHits(0);
        setRevealedCopies(new Set());
        setExploding(new Set());
        setShuffleNonce((current) => current + 1);
        setLevelCleared(false);
        setPowerLevel((current) => Math.min(finalePowerMax, current + 1));
      }, reducedMotion ? 450 : 1050);
      return;
    }
    schedule(() => {
      setLevel(finaleLevelCount);
      setPhase('boss');
      setLevelCleared(false);
      setExploding(new Set());
      setRevealedCopies(new Set());
      setPowerLevel((current) => Math.min(finalePowerMax, current + 2));
      play('god-mode', 0.76);
    }, reducedMotion ? 500 : 1200);
  };

  const fireTarget = (event: ReactPointerEvent<HTMLButtonElement>, target: ArenaTarget) => {
    event.stopPropagation();
    if (event.button !== 0 || cooldownMs > 0 || reloading || phase !== 'levels' || levelCleared || exploding.has(target.key)) return;
    fire(event.clientX, event.clientY, target.isCopy);
    if (!target.isCopy) {
      play('ricochet', 0.92 + level * 0.012);
      setShuffleNonce((current) => current + 1);
      return;
    }
    setRevealedCopies((current) => new Set(current).add(target.key));
    const nextHits = copyHits + 1;
    setCopyHits(nextHits);
    setShuffleNonce((current) => current + 1);
    addImpact(event.clientX, event.clientY, 1 + level * 0.24, false, Math.floor(effectDensity(level) * 0.55));
    if (nextHits >= copyGoal) finishCopyLevel(target.character.id, event.clientX, event.clientY, target.key);
  };

  const collectPowerUp = (event: ReactPointerEvent<HTMLButtonElement>, item: FinalePowerUp) => {
    event.stopPropagation();
    if (event.button !== 0 || !combatActive) return;
    fire(event.clientX, event.clientY, true);
    setPowerUps((current) => current.filter((candidate) => candidate.id !== item.id));
    if (item.type === 'power') setPowerLevel((current) => Math.min(finalePowerMax, current + 2));
    if (item.type === 'rapid') { setPowerLevel((current) => Math.min(finalePowerMax, current + 1)); setCooldownUntil(0); }
    if (item.type === 'shield') setShield((current) => Math.min(100, current + 45));
    if (item.type === 'medkit') setHealth((current) => Math.min(100, current + 35));
    if (item.type === 'bomb') { setScore((current) => current + level * 2500); addImpact(window.innerWidth / 2, window.innerHeight / 2, 12, true, 72); }
    setScore((current) => current + 750 * level);
    addImpact(event.clientX, event.clientY, powerLevel + 2, item.type === 'bomb');
    play('weapon-equip', 1.03 + level * 0.012);
  };

  const takeDamage = (amount: number) => {
    if (godMode || !combatActive) return;
    setHitSequence((current) => current + 1);
    play('target-hit', 0.58, 0.75);
    speak(`hit-${1 + hitSequence % 3}`);
    let remaining = amount;
    setShield((current) => { const absorbed = Math.min(current, remaining); remaining -= absorbed; return current - absorbed; });
    schedule(() => setHealth((current) => {
      const next = Math.max(0, current - remaining);
      if (next === 0) {
        setFinishedElapsedMs(Date.now() - startTimeRef.current);
        setPhase('game-over');
      }
      return next;
    }), 0);
  };

  useEffect(() => {
    if (!combatActive || level < 6) return;
    const interval = window.setInterval(() => {
      const id = ++effectIdRef.current;
      const x = 8 + seeded(id * 17 + level) * 84;
      const y = 18 + seeded(id * 31 + level) * 34;
      const bolt = { id, x, y, damage: enemyDamage(level) };
      setEnemyBolts((current) => [...current.slice(-8), bolt]);
      play('plasma-shot', 0.48 + level * 0.01, 0.32);
      schedule(() => { setEnemyBolts((current) => current.filter((candidate) => candidate.id !== id)); takeDamage(bolt.damage); }, reducedMotion ? 260 : 720);
    }, enemyFireDelay(level));
    return () => window.clearInterval(interval);
  }, [combatActive, godMode, level, phase, reducedMotion, selected.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || !combatActive || event.repeat) return;
      event.preventDefault();
      speak(`catch-${1 + Math.floor(seeded(Date.now()) * 4)}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [combatActive, selected.id]);

  const fireBoss = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (event.button !== 0 || phase !== 'boss' || cooldownMs > 0 || reloading) return;
    fire(event.clientX, event.clientY, true);
    const nextHealth = Math.max(0, bossHealth - damage);
    setBossHealth(nextHealth);
    setScore((current) => current + damage * 12);
    addImpact(event.clientX, event.clientY, 8 + powerLevel * 0.4, true, 72);
    play('target-hit', 0.65 + powerLevel * 0.02);
    if (nextHealth > 0) return;
    setFinishedElapsedMs(Date.now() - startTimeRef.current);
    setPhase('boss-defeat');
    setCooldownUntil(0);
    setScore((current) => current + 250000 + powerLevel * 5000);
    addImpact(window.innerWidth * 0.5, window.innerHeight * 0.37, 12, true, 72);
    play('vaporize', 0.62);
    schedule(() => play('record-save', 0.82), 260);
    schedule(() => setPhase('victory'), reducedMotion ? 500 : 1500);
  };

  const chooseFighter = (character: CharacterProfile) => { setSelected(character); setPhase('confirm'); play('weapon-equip', 1.03); };
  const confirmFighter = () => {
    setPhase('ready');
    play('record-save', 1.08);
    void attractAudio.play(audioAsset(`/assets/audio/characters/${selected.id}.wav`), { kind: 'shooter-voice', gain: 1 });
    schedule(() => { setFightVisible(true); play('god-mode', 0.78); }, 2450);
    schedule(() => { setFightExplosion(true); play('vaporize', 0.7); }, 3450);
    schedule(() => {
      setFightVisible(false);
      setFightExplosion(false);
      setPhase('levels');
      startTimeRef.current = Date.now();
      setClock(startTimeRef.current);
    }, 3900);
  };

  const saveScore = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = playerName.trim().slice(0, 15).toUpperCase();
    if ((phase !== 'victory' && phase !== 'game-over') || !name || recordSaved) return;
    const entry: FinaleScoreEntry = { name, score, elapsedMs, cleared: phase === 'victory' ? finaleLevelCount : level, bossDefeated: phase === 'victory', powerLevel, godMode, recordedAt: new Date().toISOString() };
    const next = rankScores([...bestScores.filter((candidate) => candidate.id !== runReportId), entry]);
    try { localStorage.setItem(finalePlayerKey, name); localStorage.setItem(finaleScoreKey, JSON.stringify(next)); } catch { /* Local score storage is optional. */ }
    setBestScores(next); setRecordSaved(true); play('record-save');
    if (runReportId) void fetch('/api/finale-scores', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cabinetId: getCabinetId(), playerName: name, runId: runReportId, renameOnly: true }) }).then(async (response) => {
      if (!response.ok) return;
      const payload = await response.json() as { scores?: FinaleScoreEntry[] };
      if (payload.scores?.length) setBestScores(rankScores(payload.scores));
    }).catch(() => undefined);
  };

  const sceneStyle = { '--weapon-accent': weapon.accent, '--power-level': powerLevel, '--level-speed': `${levelSpeed(level)}s`, '--destruction-level': level } as CSSProperties;

  return (
    <main ref={arenaRef} className={`attract-scene finale-attract${combatActive ? ' is-game-active' : ''}${phase === 'boss' ? ' is-boss-phase' : ''}${phase === 'boss-defeat' ? ' is-boss-defeat' : ''}${phase === 'victory' ? ' is-victory' : ''}${hitSequence && audioPreferences.shooterShake ? ' was-hit' : ''}${weaponId === 'sniper-rifle' ? ' is-sniping' : ''}`} aria-labelledby="finale-heading" onPointerMove={updateAim} onPointerDown={fireMiss} style={sceneStyle}>
      <img className="finale-attract__arena" src="/assets/attract/finale/finale-arena-empty.webp" alt="The Convergence towering above an island arena" />
      <div className="finale-attract__shade" aria-hidden="true" />

      {(phase === 'select' || phase === 'confirm' || phase === 'ready') && <section className="finale-player-select" aria-label="Choose your playable Smacker">
        <div className="finale-player-select__preview"><p>CONVERGENCE BREACH // CHOOSE YOUR VOICE</p><IdleFighterSprite character={selected} side="right" fastMode={false} /><h1 id="finale-heading">{selected.name}</h1><span>{selected.nickname}</span></div>
        <div className="finale-player-select__roster">
          <h2>SELECT YOUR SMACKER</h2>
          <div>{roster.map((character) => <button key={character.id} type="button" className={selected.id === character.id ? 'is-selected' : ''} onClick={() => chooseFighter(character)} disabled={phase === 'ready'}><img src={character.images[0].srcSet.split(' ')[0]} alt="" /><span>{character.name}</span></button>)}</div>
        </div>
        {phase === 'confirm' && <div className="finale-player-select__confirm" onPointerDown={(event) => event.stopPropagation()}><p>FIGHT AS</p><strong>{selected.name}?</strong><div><button type="button" onClick={confirmFighter}>CONFIRM</button><button type="button" onClick={() => setPhase('select')}>CANCEL</button></div></div>}
        {phase === 'ready' && <div className="finale-player-select__locked"><p>VOICE LINKED // PLASMA SIGNATURE LOCKED</p><strong>{selected.name} ENTERS ISLAND ZERO</strong><span>FIND THE DUPLICATE. ORIGINALS CANNOT BE HARMED.</span></div>}
        <FightImpactOverlay fight={fightVisible} explosion={fightExplosion} />
      </section>}

      <button className="finale-boss-target" type="button" aria-label="Fire at the Convergence core" disabled={phase !== 'boss'} onPointerDown={fireBoss} />
      <div className="finale-targets" aria-label="Smackers originals and one vulnerable duplicate">
        {arenaTargets.map((target, index) => {
          const targetStyle = { '--target-x': `${target.x}%`, '--target-bottom': `${target.bottom}%`, '--target-scale': `${target.scale}`, '--target-z': `${20 - target.row}`, '--target-delay': `${-seeded(index + level) * 4}s`, '--target-drift': `${8 + level * 1.8}px` } as CSSProperties;
          const copyRevealed = target.isCopy && revealedCopies.has(target.key);
          return <button key={target.key} className={`finale-target finale-target--${target.character.id}${copyRevealed ? ' is-copy' : ' is-original'}${level >= 4 ? ' is-moving' : ''}${level >= 6 ? ' is-armed' : ''}${exploding.has(target.key) ? ' is-exploding' : ''}`} style={targetStyle} type="button" aria-label={`Fire at ${target.character.name}${copyRevealed ? ' revealed duplicate' : ''}`} disabled={exploding.has(target.key) || levelCleared} onPointerDown={(event) => fireTarget(event, target)}><img src={`/assets/characters/${target.character.id}/idle/previews/${target.character.id}_idle_preview.gif?v=20260717-5`} alt={target.character.name} draggable="false" /><span>{target.character.name}</span>{copyRevealed && <i>COPY SIGNAL</i>}</button>;
        })}
      </div>

      {powerUps.map((item) => <button key={item.id} className={`finale-power-core finale-power-core--${item.type}`} type="button" aria-label={`Shoot ${powerUpNames[item.type]} power-up`} style={{ '--core-x': `${item.x}px`, '--core-y': `${item.y}px` } as CSSProperties} onPointerDown={(event) => collectPowerUp(event, item)}><i /><span>{powerUpNames[item.type]}</span></button>)}
      {enemyBolts.map((bolt) => <span key={bolt.id} className="finale-enemy-bolt" style={{ '--enemy-x': `${bolt.x}vw`, '--enemy-y': `${bolt.y}vh`, '--enemy-damage': bolt.damage } as CSSProperties}><i /></span>)}

      {combatActive && <FinaleGameHud score={score} elapsedMs={elapsedMs} level={level} phase={phase} copyHits={copyHits} copyHitsGoal={copyGoal} bossHealth={bossHealth} bossMaxHealth={finaleBossHealth} health={health} shield={shield} cooldownMs={cooldownMs} cooldownDurationMs={cooldownDurationMs} damage={damage} powerLevel={powerLevel} weaponId={weaponId} drawerOpen={drawerOpen} godMode={godMode} onDrawerToggle={() => setDrawerOpen((current) => !current)} onWeaponChange={(nextWeapon) => { setWeaponId(nextWeapon); setDrawerOpen(false); setCooldownUntil(0); play('weapon-equip', 0.9 + finaleWeapons.findIndex((candidate) => candidate.id === nextWeapon) * 0.045); }} onGodToggle={() => { setGodMode((current) => !current); setCooldownUntil(0); play('god-mode'); }} audioPreferences={audioPreferences} onAudioPreferencesChange={updateAudioPreferences} />}
      {levelCleared && <div className="finale-level-card" aria-live="assertive"><span>COPY ERASED</span><strong>LEVEL {level} CLEAR</strong><small>DESTRUCTION DOUBLES</small></div>}

      {combatActive && <div key={`${weaponId}-${firingSequence}`} className={`finale-weapon-rig finale-weapon-rig--${weapon.recoil}${firingSequence > 0 ? ' is-firing' : ''}`} aria-hidden="true"><img className="finale-weapon" src={`/assets/attract/finale/weapons/${weaponId}.webp`} alt="" draggable="false" /><i /><b /></div>}
      {combatActive && <div key={`pilot-${hitSequence}`} className={`finale-player-avatar${hitSequence > 0 ? ' is-hit' : ''}`} aria-label={`${selected.name} player status`}><img src={selected.images[0].srcSet.split(' ')[0]} alt="" /><span><b>{selected.name}</b><small>{hitSequence > 0 ? 'OUCH // PLASMA LINK HIT' : 'PLASMA LINKED'}</small></span></div>}
      {reloading && <div className="finale-reload-arm" aria-label="Reloading plasma rifle"><span><i>THE<br />SMACKERS</i></span><b>RECHARGING</b></div>}
      {combatActive && <span className="finale-aim-reticle" aria-hidden="true" />}
      {combatActive && cooldownMs > 0 && <div className="finale-cooldown" aria-live="polite"><span>{reloading ? 'LEFT ARM RELOAD // 50 SHOTS' : `CORE COOLING // LV ${powerLevel}`}</span><strong>{(cooldownMs / 1000).toFixed(1)}</strong><i style={{ '--cooldown': `${cooldownDurationMs > 0 ? cooldownMs / Math.max(cooldownDurationMs, 1050) * 100 : 0}%` } as CSSProperties} /></div>}
      {shots.map((shot) => <span key={shot.id} className={`finale-shot finale-shot--${shot.weaponId}${shot.hit ? ' is-hit' : ''}`} style={{ '--shot-origin-x': `${shot.originX}px`, '--shot-origin-y': `${shot.originY}px`, '--shot-angle': `${shot.angle}deg`, '--shot-length': `${shot.length}px`, '--shot-power': shot.power } as CSSProperties}><i /><b /></span>)}
      {impacts.map((impact) => <span key={impact.id} className={`finale-impact${impact.boss ? ' finale-impact--boss' : ''}`} style={{ '--impact-x': `${impact.x}px`, '--impact-y': `${impact.y}px`, '--impact-scale': 0.7 + impact.power * 0.2 } as CSSProperties}><i /><b />{Array.from({ length: impact.density }, (_, index) => <em key={index} style={{ '--gib-angle': `${index * (360 / impact.density) + impact.id % 19}deg`, '--gib-distance': `${65 + impact.power * 15 + index % 7 * 18}px`, '--gib-size': `${4 + impact.power * 0.8 + index % 4 * 2}px` } as CSSProperties} />)}</span>)}
      {weaponId === 'sniper-rifle' && combatActive && <div className="finale-scope" aria-hidden="true"><i /><b /></div>}
      {hitSequence > 0 && <div key={hitSequence} className="finale-player-hit" aria-hidden="true" />}

      {phase === 'game-over' && <section className="finale-clear finale-clear--loss" aria-live="assertive">
        <p>PLASMA LINK SEVERED // LEVEL {level}</p><h2>SMACKED DOWN</h2><strong>{score.toString().padStart(7, '0')} // {(elapsedMs / 1000).toFixed(1)} SEC</strong>
        <FinaleLeaderboard scores={bestScores} />
        <form className="finale-clear__record" onSubmit={saveScore} onPointerDown={(event) => event.stopPropagation()}><label htmlFor="finale-loss-player-name">PILOT NAME</label><div><input id="finale-loss-player-name" maxLength={15} value={playerName} onChange={(event) => setPlayerName(event.target.value.replace(/[^a-z0-9 _-]/gi, '').slice(0, 15))} placeholder={selected.name.toUpperCase()} /><button type="submit" disabled={recordSaved || !runReportId}>{recordSaved ? 'SAVED' : 'CLAIM SCORE'}</button></div><small>{scoreStatus}</small></form>
        <div className="finale-clear__actions"><button type="button" onClick={onRetryShooter}>RETRY SHOOTER</button><button type="button" onClick={onRestart}>RESTART ATTRACT MODE</button><button type="button" onClick={onPlayFightingGame}>PLAY FIGHTING GAME</button></div>
      </section>}
      {phase === 'victory' && <section className="finale-clear" aria-live="assertive">
        <p>ISLAND ZERO // TWENTY LEVELS CLEARED</p><h2>CONVERGENCE DEFEATED</h2><strong>{score.toString().padStart(7, '0')} // {(elapsedMs / 1000).toFixed(1)} SEC // PLASMA LV {powerLevel}</strong><span>{selected.name.toUpperCase()} BROKE THE COPY FURNACE</span>
        <FinaleLeaderboard scores={bestScores} />
        <form className="finale-clear__record" onSubmit={saveScore} onPointerDown={(event) => event.stopPropagation()}><label htmlFor="finale-player-name">PILOT NAME</label><div><input id="finale-player-name" maxLength={15} value={playerName} onChange={(event) => setPlayerName(event.target.value.replace(/[^a-z0-9 _-]/gi, '').slice(0, 15))} placeholder={selected.name.toUpperCase()} /><button type="submit" disabled={recordSaved || !runReportId}>{recordSaved ? 'SAVED' : 'CLAIM SCORE'}</button></div><small>{scoreStatus}</small></form>
        <div className="finale-clear__actions"><button type="button" onPointerDown={(event) => event.stopPropagation()} onClick={onRetryShooter}>RETRY SHOOTER</button><button type="button" onPointerDown={(event) => event.stopPropagation()} onClick={onRestart}>RESTART ATTRACT MODE</button><button type="button" onPointerDown={(event) => event.stopPropagation()} onClick={onPlayFightingGame}>PLAY FIGHTING GAME</button></div>
      </section>}
    </main>
  );
}
